import { ReploError } from "schemas/reploError";
import * as z from "zod";

/**
 * A Canvas set, as stored in a `*.canvas.json` file. The agent reads and writes
 * this file directly, so every field is one it can get wrong: nothing here is
 * derived, and nothing is stored two ways.
 *
 * Units, fixed for the life of the format:
 * - `box`, `crop`, gradient points and `paragraph.height` are fractions (of the
 *   frame, of the parent group, or of the image's natural size).
 * - Sizes in px (`fontSize`, `radius`, `stroke.width`, `shadow.*`) are pixels
 *   at the frame's own `size`, so they rescale with the frame.
 * - `letterSpacing` is in em; `lineHeight` is a multiple of the font size.
 * - `rotation` is degrees clockwise about the box centre, applied after flips.
 *
 * Fabric is the renderer, not the format: no field holds a Fabric record.
 */

/* -------------------------------------------------------------------------- */
/* Primitives                                                                 */
/* -------------------------------------------------------------------------- */

/** `[x, y, w, h]` as fractions of the frame (or of the parent group), so a layer survives a format change. */
export const canvasBoxSchema = z
  .array(z.number())
  .length(4)
  .describe("CanvasBox");

/** Any CSS colour; hex (`#rrggbb` or `#rrggbbaa`) is what the editor writes. */
const colorSchema = z.string().min(1);

/** A point inside the layer's box, as fractions of its width and height. */
const pointSchema = z.tuple([z.number(), z.number()]);

/** A colour at a point along the gradient; a translucent stop says so with its colour's alpha. */
export const canvasGradientStopSchema = z
  .object({
    offset: z.number().min(0).max(1),
    color: colorSchema,
  })
  .describe("CanvasGradientStop");

/**
 * A gradient laid over the layer's box. Points are box fractions, so a
 * gradient stretches with its layer; a radial one on a non-square box is an
 * ellipse.
 */
export const canvasGradientSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("linear"),
      from: pointSchema,
      to: pointSchema,
      stops: z.array(canvasGradientStopSchema).min(2),
    }),
    z.object({
      type: z.literal("radial"),
      center: pointSchema,
      radius: z.number().positive(),
      stops: z.array(canvasGradientStopSchema).min(2),
    }),
  ])
  .describe("CanvasGradient");

/** A colour, or a gradient. One per layer: overlays are more layers, not a stack of paints. */
export const canvasFillSchema = z
  .union([colorSchema, canvasGradientSchema])
  .describe("CanvasFill");

export const canvasStrokeSchema = z
  .object({
    color: colorSchema,
    width: z.number().positive(),
    /** Dash and gap lengths in px; absent is a solid line. */
    dash: z.array(z.number().nonnegative()).min(1).optional(),
    cap: z.enum(["butt", "round", "square"]).optional(),
    join: z.enum(["miter", "round", "bevel"]).optional(),
  })
  .describe("CanvasStroke");

export const canvasShadowSchema = z
  .object({
    color: colorSchema,
    x: z.number(),
    y: z.number(),
    blur: z.number().nonnegative(),
  })
  .describe("CanvasShadow");

/**
 * The visible window into an image, as fractions of the source's natural size,
 * so a crop survives the source being re-minted at another resolution.
 */
export const canvasCropSchema = z
  .object({ x: z.number(), y: z.number(), w: z.number(), h: z.number() })
  .describe("CanvasCrop");

/**
 * A durable reference to image bytes. Never a URL for platform content: Files
 * URLs expire, so a resolver mints a fresh signed URL on load.
 */
export const canvasImageRefSchema = z
  .discriminatedUnion("source", [
    z.object({ source: z.literal("asset"), id: z.string().min(1) }),
    z.object({ source: z.literal("file"), id: z.string().min(1) }),
    z.object({
      source: z.literal("product"),
      productId: z.string().min(1),
      imageIndex: z.number().int().nonnegative(),
    }),
    // Seeded templates: bytes addressed by a plain URL.
    z.object({ source: z.literal("url"), url: z.string().min(1) }),
  ])
  .describe("CanvasImageRef");

/** Fabric normalizes relative, arc and shorthand commands to these absolute commands. */
export const canvasPathCommandSchema = z
  .union([
    z.tuple([z.literal("M"), z.number().finite(), z.number().finite()]),
    z.tuple([z.literal("L"), z.number().finite(), z.number().finite()]),
    z.tuple([
      z.literal("Q"),
      z.number().finite(),
      z.number().finite(),
      z.number().finite(),
      z.number().finite(),
    ]),
    z.tuple([
      z.literal("C"),
      z.number().finite(),
      z.number().finite(),
      z.number().finite(),
      z.number().finite(),
      z.number().finite(),
      z.number().finite(),
    ]),
    z.tuple([z.literal("Z")]),
  ])
  .describe("CanvasPathCommand");

const pathCommandsSchema = (min: number) => {
  return z
    .array(canvasPathCommandSchema)
    .min(min)
    .max(10000)
    .refine(
      (commands) => commands[0]?.[0] === "M",
      "A path must begin with a move command",
    );
};

/* -------------------------------------------------------------------------- */
/* Layers                                                                     */
/* -------------------------------------------------------------------------- */

const layerBase = {
  id: z.string().min(1),
  name: z.string().optional(),
  /** The template role this layer plays (`headline`, `photo`, `logo`, `cta`, …). */
  slot: z.string().min(1).optional(),
  box: canvasBoxSchema,
  rotation: z.number().optional(),
  skewX: z.number().finite().optional(),
  opacity: z.number().min(0).max(1).optional(),
  locked: z.boolean().optional(),
  hidden: z.boolean().optional(),
  flipX: z.boolean().optional(),
  flipY: z.boolean().optional(),
};

/**
 * How a picture meets its box, as CSS `object-fit`: `cover` crops to fill,
 * `contain` letterboxes, `fill` stretches the crop to the box's shape.
 */
export const canvasImageFitSchema = z
  .enum(["cover", "contain", "fill"])
  .describe("CanvasImageFit");

export const canvasImageLayerSchema = z
  .object({
    ...layerBase,
    kind: z.literal("image"),
    ref: canvasImageRefSchema,
    fit: canvasImageFitSchema,
    crop: canvasCropSchema.optional(),
    radius: z.number().nonnegative().optional(),
    shadow: canvasShadowSchema.optional(),
  })
  .describe("CanvasImageLayer");

/**
 * Character styling. A text layer carries the whole set; a run overrides any
 * of it for a stretch of characters.
 */
const textStyleShape = {
  font: z.string().min(1),
  weight: z.number().optional(),
  fontSize: z.number().positive(),
  color: canvasFillSchema,
  italic: z.boolean().optional(),
  underline: z.boolean().optional(),
  strikethrough: z.boolean().optional(),
};

/** `start`/`end` index `text` in UTF-16 code units, as JavaScript strings do; `end` is exclusive. */
export const canvasTextRunSchema = z
  .object({
    start: z.number().int().nonnegative(),
    end: z.number().int().positive(),
    ...z.object(textStyleShape).partial().shape,
    /** A plain colour: Fabric paints one fill per character, never a gradient. */
    color: colorSchema.optional(),
  })
  .refine((run) => run.end > run.start, "A run must cover a character")
  .describe("CanvasTextRun");

/**
 * Text that wraps at the box width inside a frame `height` tall (a fraction of
 * the frame): `shrink` lowers the type size (never above `maxFontSize`) until the
 * copy fits, `grow` extends the frame to the copy, `none` lets it overflow.
 * Without a paragraph, text is one auto-sized line.
 */
export const canvasParagraphSchema = z
  .object({
    height: z.number().positive(),
    fit: z.enum(["shrink", "grow", "none"]),
    maxFontSize: z.number().positive().optional(),
  })
  .describe("CanvasParagraph");

/** Text set along a curve; the path's own coordinates are fitted to the box, and `startOffset` is a fraction of its length. */
export const canvasTextPathSchema = z
  .object({
    commands: pathCommandsSchema(2),
    startOffset: z.number().finite().min(-1).max(1).optional(),
    side: z.enum(["left", "right"]).optional(),
    align: z.enum(["baseline", "center", "ascender", "descender"]).optional(),
  })
  .describe("CanvasTextPath");

export const canvasTextLayerSchema = z
  .object({
    ...layerBase,
    kind: z.literal("text"),
    text: z.string(),
    ...textStyleShape,
    align: z.enum(["left", "center", "right", "justify"]).optional(),
    lineHeight: z.number().positive().optional(),
    letterSpacing: z.number().optional(),
    paragraph: canvasParagraphSchema.optional(),
    path: canvasTextPathSchema.optional(),
    runs: z.array(canvasTextRunSchema).optional(),
    /** The colour block behind each line, for legibility over a photo. */
    highlight: z.object({ color: colorSchema }).optional(),
    stroke: canvasStrokeSchema.pick({ color: true, width: true }).optional(),
    shadow: canvasShadowSchema.optional(),
  })
  .refine(
    (layer) => !layer.path || !layer.paragraph,
    "Text on a path is a single line, not a paragraph",
  )
  .describe("CanvasTextLayer");

/** What a shape layer draws, fitted to its box. `path` and `polygon` coordinates are the shape's own and are scaled to the box. */
export const canvasShapeSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("rect"),
      radius: z.number().nonnegative().optional(),
    }),
    z.object({ type: z.literal("ellipse") }),
    z.object({ type: z.literal("line") }),
    z.object({
      type: z.literal("path"),
      commands: pathCommandsSchema(1),
      fillRule: z.enum(["nonzero", "evenodd"]).optional(),
    }),
    z.object({
      type: z.literal("polygon"),
      points: z
        .array(z.object({ x: z.number().finite(), y: z.number().finite() }))
        .min(3)
        .max(10000),
      fillRule: z.enum(["nonzero", "evenodd"]).optional(),
    }),
  ])
  .describe("CanvasShape");

export const canvasShapeLayerSchema = z
  .object({
    ...layerBase,
    kind: z.literal("shape"),
    shape: canvasShapeSchema,
    /** Absent paints nothing; a line ignores it. */
    fill: canvasFillSchema.optional(),
    stroke: canvasStrokeSchema.optional(),
  })
  .describe("CanvasShapeLayer");

/** Declared by hand because `children` refers back to the layer union; the Zod schema below matches it field for field. */
export type CanvasGroupLayer = {
  id: string;
  name?: string;
  slot?: string;
  box: number[];
  rotation?: number;
  skewX?: number;
  opacity?: number;
  locked?: boolean;
  hidden?: boolean;
  flipX?: boolean;
  flipY?: boolean;
  kind: "group";
  /** The group's own width and height before scaling, which keeps rotated children upright through a non-uniform resize. */
  intrinsicSize?: [number, number];
  /** Child boxes are fractions of the group's box. */
  children: CanvasLayer[];
};

export type CanvasLayer =
  | z.infer<typeof canvasImageLayerSchema>
  | z.infer<typeof canvasTextLayerSchema>
  | z.infer<typeof canvasShapeLayerSchema>
  | CanvasGroupLayer;

export const canvasGroupLayerSchema: z.ZodType<CanvasGroupLayer> = z
  .object({
    ...layerBase,
    kind: z.literal("group"),
    intrinsicSize: z
      .tuple([z.number().finite().positive(), z.number().finite().positive()])
      .optional(),
    get children() {
      return z.array(canvasLayerSchema).min(1);
    },
  })
  .describe("CanvasGroupLayer");

export const canvasLayerSchema: z.ZodType<CanvasLayer> = z
  .union([
    canvasImageLayerSchema,
    canvasTextLayerSchema,
    canvasShapeLayerSchema,
    canvasGroupLayerSchema,
  ])
  .describe("CanvasLayer");

/* -------------------------------------------------------------------------- */
/* Frames                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Pixel dimensions plus, for print sizes, the DPI the catalog listed — which is
 * what makes an A4 export 2480×3508 rather than a 2× bitmap.
 */
export const canvasFrameSizeSchema = z
  .object({
    w: z.number().int().positive(),
    h: z.number().int().positive(),
    dpi: z.number().int().positive().optional(),
    /** The catalog label ("Instagram story / reel"), so a frame can name its size. */
    label: z.string().optional(),
  })
  .describe("CanvasFrameSize");

/**
 * Inset of the safe zone from each edge, as a fraction of the frame: a story
 * frame reserves 14% at the top and 35% at the bottom for platform chrome.
 */
export const canvasSafeAreaSchema = z
  .object({
    top: z.number(),
    bottom: z.number(),
    left: z.number(),
    right: z.number(),
  })
  .describe("CanvasSafeArea");

export const canvasFrameGenerationSchema = z
  .object({
    id: z.uuid(),
    startedAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    status: z.enum([
      "preparing",
      "generating",
      "assembling",
      "completed",
      "failed",
    ]),
    jobId: z.uuid().optional(),
    error: z.string().max(1000).optional(),
    phases: z
      .array(z.enum(["design", "split"]))
      .min(1)
      .max(2)
      .optional()
      .describe(
        "What this run does, in order: render the finished ad, split it into editable layers, or both. Absent means both.",
      ),
  })
  .describe("CanvasFrameGeneration");

export const canvasFrameSchema = z
  .object({
    id: z.string().min(1),
    name: z.string(),
    /**
     * The grid row this frame sits in and its place along it. A frame's
     * position is these two numbers alone: the editor lays rows out top to
     * bottom and never stores the coordinates it draws them at.
     */
    row: z.number().int().nonnegative(),
    col: z.number().int().nonnegative(),
    size: canvasFrameSizeSchema,
    safeArea: canvasSafeAreaSchema.nullable().default(null),
    /** The artboard fill: a frame field, not a layer, so it is never selectable. Null is white. */
    background: canvasFillSchema.nullable().default(null),
    /** Back to front. */
    layers: z.array(canvasLayerSchema),
    /** Id of the template this frame was built from, for QA and "recreate". */
    templateId: z.string().optional(),
    /** `reference` holds another brand's ad for comparison; it is never exported or used as the cover. Absent is the set's own artwork. */
    role: z.enum(["reference"]).optional(),
    /** Execution metadata is separate from artwork and never exported. */
    generation: canvasFrameGenerationSchema.optional(),
  })
  .describe("CanvasFrame");

/** Frames in reading order: rows top to bottom, then along each row. */
export function sortFramesByGrid<Frame extends { row: number; col: number }>(
  frames: Frame[],
): Frame[] {
  return [...frames].sort((left, right) => {
    return left.row - right.row || left.col - right.col;
  });
}

/* -------------------------------------------------------------------------- */
/* Items                                                                      */
/* -------------------------------------------------------------------------- */

/** The most items one set holds; a concept is tens of steps, not thousands. */
export const CANVAS_ITEMS_MAX = 200;

/** The longest HTML one step holds: a mockup of one screen, not a page. */
export const CANVAS_STEP_HTML_MAX = 20_000;

/** The longest label a step wears: a word or two of what it is for. */
export const CANVAS_STEP_LABEL_MAX = 80;

/** One screen of a quiz concept: an HTML fragment with no stored type, because what it is comes from what is on it. Never published or linked to a site file. */
export const canvasStepItemSchema = z
  .object({
    kind: z.literal("step"),
    id: z.string().min(1),
    /** Top-left corner in world pixels, the same space the frame grid is drawn in. */
    x: z.number(),
    y: z.number(),
    /** The screen: its copy, structure and styling. Elements with a `data-id` can carry arrows. */
    html: z.string().max(CANVAS_STEP_HTML_MAX),
    /** What the screen is for, in the person's own words. Never a type. */
    label: z.string().max(CANVAS_STEP_LABEL_MAX).optional(),
    /** Choices are pick-any. They then carry no arrows of their own. */
    multi: z.boolean().optional(),
    /** `inspiration` is a look to follow, not a screen to build. Absent means a screen. */
    role: z.enum(["screen", "inspiration"]).optional(),
  })
  .describe("CanvasStepItem");

/** "The visitor goes from this step to that one", from the step or from one element (`data-id`) on it. */
export const canvasEdgeItemSchema = z
  .object({
    kind: z.literal("edge"),
    id: z.string().min(1),
    from: z.string().min(1),
    fromElement: z.string().min(1).optional(),
    to: z.string().min(1),
    /** Free text for a rule about an earlier answer; absent means always. */
    condition: z.string().max(300).optional(),
  })
  .describe("CanvasEdgeItem");

/** A named group of frames and items. Its rectangle is derived from its members. */
export const canvasSectionItemSchema = z
  .object({
    kind: z.literal("section"),
    id: z.string().min(1),
    name: z.string().max(200),
    members: z.array(z.string().min(1)),
  })
  .describe("CanvasSectionItem");

export const canvasPageItemSchema = z
  .object({
    kind: z.literal("page"),
    id: z.string().min(1),
    x: z.number(),
    y: z.number(),
    shareId: z.uuid(),
    device: z.enum(["desktop", "tablet", "mobile"]),
    /** Body height in CSS pixels; absent uses the device preset. */
    height: z.number().finite().positive().optional(),
    label: z.string().max(200).optional(),
  })
  .describe("CanvasPageItem");

export const canvasItemSchema = z
  .discriminatedUnion("kind", [
    canvasStepItemSchema,
    canvasEdgeItemSchema,
    canvasSectionItemSchema,
    canvasPageItemSchema,
  ])
  .describe("CanvasItem");

/* -------------------------------------------------------------------------- */
/* Sets                                                                       */
/* -------------------------------------------------------------------------- */

/** The format this build writes. Older files are upgraded by `migrations.ts`; newer ones are refused. */
export const CANVAS_FORMAT_VERSION = 3;

/**
 * A whole `*.canvas.json` file. Its title is the filename; its preview image
 * lives beside the file in storage, never inside it.
 */
export const canvasSetSchema = z
  .object({
    version: z.literal(CANVAS_FORMAT_VERSION),
    frames: z.array(canvasFrameSchema),
    /** Everything that is not an artboard: quiz steps, arrows, sections, page previews. */
    items: z.array(canvasItemSchema).max(CANVAS_ITEMS_MAX).default([]),
    /** The frame the set's preview features; null features the first few. */
    coverFrameId: z.string().min(1).nullable().default(null),
  })
  .refine(
    (set) => {
      return (
        set.coverFrameId === null ||
        set.frames.some((frame) => frame.id === set.coverFrameId)
      );
    },
    {
      message: "The cover frame must belong to the set",
      path: ["coverFrameId"],
    },
  )
  .describe("CanvasSet");

/** A reference frame is shown for comparison, never exported unless picked on purpose. */
export function isReferenceFrame(frame: { role?: "reference" }) {
  return frame.role === "reference";
}

/** A blank set, as "New set" writes it before the first frame exists. */
export function emptyCanvasSet(): z.infer<typeof canvasSetSchema> {
  return {
    version: CANVAS_FORMAT_VERSION,
    frames: [],
    items: [],
    coverFrameId: null,
  };
}

export class CanvasSetUnreadableError extends ReploError {}
